import React, { useCallback, useMemo, useState } from 'react';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { CompactOrderableItem } from './compact-orderable-item';
import { createStyles } from '@/contexts/theme-context';
import { DSRToolingActionSheet } from './dsr-tooling-action-sheet';
import { findFirstPaidSearchSku } from '@/utils/ads';
import {
  Keyboard,
  StyleProp,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { ListViewOrderableItem } from './list-view-orderable-item';
import { MultiOptionOrderableItem } from './multi-option-orderable-item';
import { NoteType, OrderableItemLabelType } from '@/enums';
import { OrderableItemLabels } from './orderable-item-labels';
import { OrderableItemListItemContent } from './orderable-item-list-item-content';
import { PAID_SEARCH_BORDER_WIDTH } from '@/components/ads/paid-search-item-header';
import { QuantityStateType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { RenderActionsFn } from './item-detail-actions';
import { SalesRepInfo, SalesRepInfoSize } from './sales-rep-info';
import { SampleRequest } from '@/components/ads/sample-request';
import { SingleOptionOrderableItem } from './single-option-orderable-item';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useItemQuantityState } from '@/stores/new-order-quantity-store';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useNavigateToDSRTooling } from '@/hooks/use-dsr-tooling-navigation';
import { useOrderGuideSettingsContext } from '@/contexts/order-guide-settings-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useUserContext } from '@/contexts/user-context';

const TOTAL_HORIZONTAL_PADDING = 16;
export const INNER_HORIZONTAL_PADDING =
  TOTAL_HORIZONTAL_PADDING - PAID_SEARCH_BORDER_WIDTH;

interface BaseProps {
  action?: React.ReactNode;
  availabilityMap?: AvailabilityMap;
  canDecrementToZero?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  disableTextInput?: boolean;
  hideSampleRequest?: boolean;
  inDraggableSheet?: boolean;
  isCompactView?: boolean;
  isFocused: boolean;
  isInboundOrder?: boolean;
  itemStyleOverrides?: StyleProp<ViewStyle>;
  onNotePress?: (metadata: ItemListOptionMetadata, type?: NoteType) => void;
  option: ItemListOption;
  quantityState?: QuantityStateType;
  renderActions?: RenderActionsFn;
  renderLastOrderedMessage?: boolean;
  renderNote?: (skuMetadata: ItemListOptionMetadata) => React.ReactNode;
  renderOrderGuideButton?: (variantPackUUID: string) => React.ReactNode;
  renderParLevels?: boolean;
  rightComponent?: React.ReactNode;
  showListView?: boolean;
  showTooltipOnMount?: boolean;
  showVariantPackOrderInfo?: boolean;
  source: string;
  updateQuantity?: UpdateQuantityFn;
  visibleLabels?: OrderableItemLabelType[];
}

type OptionPressType = (option: ItemListOption) => void;
export type Props = BaseProps &
  (
    | {
        onOptionsPress?: undefined | OptionPressType;
        showSingleOption?: false;
      }
    | {
        onOptionsPress: OptionPressType;
        showSingleOption: true;
      }
  );

export const OrderableItemListItem: React.FC<Props> = ({
  action,
  availabilityMap,
  canDecrementToZero,
  containerStyle,
  contentStyle,
  disabled,
  disableTextInput,
  hideSampleRequest = false,
  inDraggableSheet,
  isCompactView = false,
  isFocused,
  isInboundOrder = false,
  itemStyleOverrides,
  onNotePress,
  onOptionsPress,
  option,
  renderNote,
  quantityState,
  renderActions,
  renderLastOrderedMessage = false,
  renderOrderGuideButton,
  renderParLevels = false,
  rightComponent,
  showListView = false,
  showSingleOption,
  showTooltipOnMount = false,
  showVariantPackOrderInfo = false,
  source,
  updateQuantity,
  visibleLabels,
}) => {
  const [styles] = useStyles();
  const isKeyboardVisible = useKeyboardVisibility();
  const salesRepMode = useSalesRepMode();
  const { listDisplaySize } = useOrderGuideSettingsContext();
  const { isGuestMode } = useUserContext();
  const [
    dsrToolingActionSheetItemMetadata,
    setDSRToolingActionSheetItemMetadata,
  ] = useState<ItemListOptionMetadata | null>(null);
  const navigateToDSRTooling = useNavigateToDSRTooling();

  const metadata = option.metadata!;
  const isPaidSearchItem = useMemo(
    () => Boolean(findFirstPaidSearchSku(option)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [option.metadata?.skus],
  );
  const shouldHideSampleRequest = isGuestMode || hideSampleRequest;

  const renderOrderGuideButtonAction = showVariantPackOrderInfo
    ? undefined
    : renderOrderGuideButton;

  const handleSalesRepPress = useCallback(
    (skuMetadata: ItemListOptionMetadata) => {
      setDSRToolingActionSheetItemMetadata(skuMetadata);
    },
    [setDSRToolingActionSheetItemMetadata],
  );

  if (showListView) {
    return (
      <>
        <ListViewOrderableItem
          action={action}
          availabilityMap={availabilityMap}
          canDecrementToZero={canDecrementToZero}
          containerStyle={containerStyle}
          disabled={disabled}
          disableTextInput={disableTextInput}
          hideSampleRequest={shouldHideSampleRequest}
          inDraggableSheet={inDraggableSheet}
          isFocused={isFocused}
          isInboundOrder={isInboundOrder}
          isPaidSearchItem={isPaidSearchItem}
          listDisplaySize={listDisplaySize}
          onOptionsPress={onOptionsPress}
          option={option}
          quantityState={quantityState}
          renderActions={renderActions}
          renderNote={renderNote}
          renderOrderGuideButtonAction={renderOrderGuideButtonAction}
          renderSalesRepContent={(skuMetadata) =>
            salesRepMode ? (
              <SalesRepInfo
                maxCells={6}
                onPress={() => handleSalesRepPress(skuMetadata)}
                size={SalesRepInfoSize.LARGE}
                variantPackUUID={skuMetadata.variantPackUUID}
              />
            ) : null
          }
          showSingleOption={showSingleOption}
          showTooltipOnMount={showTooltipOnMount}
          showVariantPackOrderInfo={showVariantPackOrderInfo}
          source={source}
          updateQuantity={updateQuantity}
          visibleLabels={visibleLabels}
        />
        {dsrToolingActionSheetItemMetadata ? (
          <DSRToolingActionSheet
            itemMetadata={dsrToolingActionSheetItemMetadata}
            onClose={() => {
              setDSRToolingActionSheetItemMetadata(null);
            }}
          />
        ) : null}
      </>
    );
  }

  const nonMinimalOptionList = showSingleOption ? (
    <SingleOptionOrderableItem
      availabilityMap={availabilityMap}
      canDecrementToZero={canDecrementToZero}
      disabled={disabled}
      disableTextInput={disableTextInput}
      inDraggableSheet={inDraggableSheet}
      isFocused={isFocused}
      metadata={metadata}
      onOptionsPress={onOptionsPress}
      option={option}
      quantityState={quantityState}
      renderActions={renderActions}
      renderLastOrderedMessage={renderLastOrderedMessage}
      renderOrderGuideButton={renderOrderGuideButtonAction}
      renderParLevels={renderParLevels}
      renderSalesRepContent={(skuMetadata) =>
        salesRepMode ? (
          <SalesRepInfo
            onPress={() => {
              const didNavigate = navigateToDSRTooling({
                itemMetadata: skuMetadata,
              });
              if (!didNavigate) {
                setDSRToolingActionSheetItemMetadata(skuMetadata);
              }
            }}
            variantPackUUID={skuMetadata.variantPackUUID}
          />
        ) : null
      }
      sampleRequestButton={
        <SampleRequest
          option={option}
          source={source}
          style={styles.sampleRequestButtonGrid}
        />
      }
      shouldHideSampleRequest={shouldHideSampleRequest}
      showTooltipOnMount={showTooltipOnMount}
      showVariantPackOrderInfo={showVariantPackOrderInfo}
      skus={metadata.skus}
      updateQuantity={updateQuantity}
    />
  ) : (
    <MultiOptionOrderableItem
      availabilityMap={availabilityMap}
      canDecrementToZero={canDecrementToZero}
      disabled={disabled}
      disableTextInput={disableTextInput}
      inDraggableSheet={inDraggableSheet}
      isFocused={isFocused}
      isInboundOrder={isInboundOrder}
      onNotePress={onNotePress}
      option={option}
      quantityState={quantityState}
      renderActions={renderActions}
      renderLastOrderedMessage={renderLastOrderedMessage}
      renderNote={renderNote}
      renderOrderGuideButtonAction={renderOrderGuideButtonAction}
      renderParLevels={renderParLevels}
      renderSalesRepContent={(skuMetadata) =>
        salesRepMode ? (
          <SalesRepInfo
            maxCells={6}
            onPress={() => {
              const didNavigate = navigateToDSRTooling({
                itemMetadata: skuMetadata,
              });
              if (!didNavigate) {
                setDSRToolingActionSheetItemMetadata(skuMetadata);
              }
            }}
            variantPackUUID={skuMetadata.variantPackUUID}
          />
        ) : null
      }
      sampleRequestButton={
        <SampleRequest
          option={option}
          source={source}
          style={styles.sampleRequestButtonGrid}
        />
      }
      shouldHideSampleRequest={shouldHideSampleRequest}
      showTooltipOnMount={showTooltipOnMount}
      showVariantPackOrderInfo={showVariantPackOrderInfo}
      skus={metadata.skus}
      updateQuantity={updateQuantity}
    />
  );

  return (
    <>
      <TouchableWithoutFeedback
        disabled={!isKeyboardVisible}
        onPress={Keyboard.dismiss}
      >
        <View
          style={[
            styles.base,
            isInboundOrder && !isCompactView ? styles.minimalBase : null,
            isPaidSearchItem ? styles.paidSearch : null,
            containerStyle,
            itemStyleOverrides,
          ]}
          testID="orderable-item-list-item"
        >
          <View
            style={[styles.mainContent, contentStyle]}
            testID={`vp:${option.metadata?.variantPackUUID}`}
          >
            {isCompactView ? (
              <View style={styles.compactViewListWrapper}>
                <OrderableItemListItemContent
                  containerStyle={styles.topRow}
                  isCompactView
                  listDisplaySize={listDisplaySize}
                  option={option}
                  rightComponent={rightComponent}
                />
                <View style={styles.flex}>
                  <OrderableItemLabels
                    option={option}
                    visibleLabels={visibleLabels}
                  />
                  <CompactOrderableItem
                    availabilityMap={availabilityMap}
                    canDecrementToZero={canDecrementToZero}
                    disabled={disabled}
                    disableTextInput={disableTextInput}
                    headingNumberOfLines={
                      option.metadata?.badges?.length ? 1 : 2
                    }
                    inDraggableSheet={inDraggableSheet}
                    isFocused={isFocused}
                    quantityState={quantityState}
                    renderActions={renderActions}
                    renderOrderGuideButtonAction={renderOrderGuideButtonAction}
                    showTooltipOnMount={showTooltipOnMount}
                    showVariantPackOrderInfo={showVariantPackOrderInfo}
                    skus={metadata.skus}
                    updateQuantity={updateQuantity}
                  />
                </View>
              </View>
            ) : (
              <>
                <View style={styles.topRowContainer}>
                  <OrderableItemLabels
                    option={option}
                    visibleLabels={visibleLabels}
                  />
                  <OrderableItemListItemContent
                    containerStyle={styles.topRow}
                    listDisplaySize={listDisplaySize}
                    option={option}
                    rightComponent={rightComponent}
                  />
                </View>
                {nonMinimalOptionList}
              </>
            )}
          </View>
        </View>
      </TouchableWithoutFeedback>
      {dsrToolingActionSheetItemMetadata ? (
        <DSRToolingActionSheet
          itemMetadata={dsrToolingActionSheetItemMetadata}
          onClose={() => {
            setDSRToolingActionSheetItemMetadata(null);
          }}
        />
      ) : null}
    </>
  );
};

// Plain Omit would collapse Props' discriminated union (onOptionsPress /
// showSingleOption); distributing keeps each branch intact.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/**
 * Variant that subscribes to its own row's quantity entries instead of
 * receiving the whole QuantityStateType from a parent — parents using this
 * don't need to read the quantity state (and re-render on every dispatch)
 * just to feed their rows.
 */
export const OrderableItemListItemWithRowQuantity: React.FC<
  DistributiveOmit<Props, 'quantityState'>
> = (props) => {
  const skus = props.option.metadata?.skus;
  const rowVariantPackUUIDs = useMemo(
    () => skus?.map((sku) => sku.variantPackUUID) ?? [],
    [skus],
  );
  const quantityState = useItemQuantityState(rowVariantPackUUIDs);

  return <OrderableItemListItem {...props} quantityState={quantityState} />;
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: PAID_SEARCH_BORDER_WIDTH,
    paddingVertical: 8,
  },
  compactViewListWrapper: {
    flexDirection: 'row',
    gap: 12,
  },
  flex: {
    flex: 1,
  },
  mainContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    paddingHorizontal: INNER_HORIZONTAL_PADDING,
    paddingVertical: 8,
  },
  minimalBase: {
    marginBottom: 12,
  },
  paidSearch: {
    backgroundColor: theme.colors.accent,
    paddingBottom: PAID_SEARCH_BORDER_WIDTH,
  },
  sampleRequestButtonGrid: {
    marginTop: 16,
  },
  topRow: {
    justifyContent: 'space-between',
  },
  topRowContainer: {
    flex: 1,
    marginBottom: 12,
  },
}));
